<template>
  <div class="relative z-0 flex h-full mb-5 overflow-hidden">
    <div v-if="is_loading" class="absolute bottom-1 right-2">
      <Spinner />
    </div>
    <Form
      ref="formUpdateTeam"
      v-slot="{ values }"
      class="flex-1 flex flex-col h-full relative z-0 overflow-y-auto focus:outline-none xl:order-last pt-8 pl-8 pr-8 pb-1.5"
      :validation-schema="new_team_schema"
      :initial-values="initial_values"
      @submit="updateTeam">
      <main class="flex flex-col items-start w-full">
        <div class="justify-self-start text-[20px] text-2xl pb-8 text-light-grey-text dark:text-dark-body-text">
          {{ $t("Update Team") }}
        </div>
        <div class="flex flex-col w-full gap-2">
          <TextInput
            show-label
            classes="sm:w-1/3 w-full"
            :label="$t('Team Name')"
            autocomplete="off"
            input-classes="dark:bg-dark-box-background bg-light-box-background dark:text-dark-body-text px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-md"
            type="text"
            :is-box-background="false"
            name="name" />
          <TextArea
            classes="sm:w-1/3 w-full mt-3"
            :rows="8"
            input-classes="dark:bg-dark-box-background bg-light-box-background dark:text-dark-body-text px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-md"
            show-label
            :label="$t('Description')"
            name="description"
            :value="values.description" />
        </div>

        <div class="flex flex-col items-start w-full gap-2 mt-10 mb-4 sm:flex-row sm:justify-between sm:items-end">
          <div class="font-medium sm:justify-self-start text-md text-light-grey-text dark:text-dark-body-text">
            {{ $t("Team Members") }}
            <p class="mt-2 text-sm text-gray-500">
              {{ $t("Select team") }} {{ values.name }} {{ $t("members and set their roles") }}
            </p>
          </div>
          <div class="flex flex-row justify-between items-center gap-2 sm:ml-auto">
            <input
              id="search"
              v-model="searched_user"
              autocomplete="off"
              type="text"
              name="search"
              class="w-full px-3 py-2 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm text-md dark:bg-dark-box-background bg-light-box-background dark:text-dark-body-text focus:outline-none focus:ring-blue-light focus:border-blue-light"
              :placeholder="$t('Find team members')">

            <SelectInput
              :disabled="teams.length === 0"
              name="type"
              :items="teams"
              label-attr="name"
              :allow-null="true"
              :classes="'w-full'"
              :value-attr="null"
              :label="$t('Teams')"
              :show-label="false"
              :placeholder="$t('Teams')"
              :is-box-background="true"
              input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              @update:currentItem="
                ($event) => {
                  selected_team = $event
                }
              " />
          </div>
        </div>
      </main>
      <!-- Testing User Table -->
      <div class="max-h-[500px] min-w-full border border-light-divider-border dark:border-dark-divider-border rounded-md">
        <table class="relative min-w-full">
          <thead
            class="w-full transition ease-in-out bg-light-box-background dark:bg-dark-box-background duration-side-menu-transition-duration">
            <draggable
              v-model="headers"
              filter=".ignore-elements"
              :component-data="{ type: 'transition-group', name: !drag ? 'flip-list' : null }"
              v-bind="dragOptions"
              item-key="name"
              class="max-w-full text-left transition ease-in-out border-light-divider-border dark:border-dark-divider-border text-common-grey duration-side-menu-transition-duration"
              tag="tr"
              @start="drag = true"
              @end="drag = false">
              <template #item="{ element, index }">
                <th v-if="element.type == 'checkbox'" class="!rounded-tl-md ignore-elements px-6 py-3 tracking-wider">
                  <input
                    v-model="select_all_users"
                    type="checkbox"
                    class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
                </th>
                <th
                  v-else-if="element.type == 'header' || element.type == 'radio' || element.type == 'avatar'"
                  :class="{ '!rounded-tr-md': index == headers.length - 1 || index == 0 }"
                  class="px-6 py-3 font-normal whitespace-nowrap">
                  {{ element.id }}
                </th>
                <th v-else-if="element.type == 'nonHeader'" class="px-6 py-3 tracking-wider rounded-tr-md whitespace-nowrap" />
              </template>
            </draggable>
          </thead>
          <tbody
            class="transition ease-in-out divide-y rounded-lg dark:border-dark-divider-border bg-light-screen-background divide-light-divider-border dark:divide-dark-divider-border dark:bg-dark-screen-background duration-side-menu-transition-duration">
            <tr
              v-for="(user, index) in users"
              :key="user.id"
              class="text-sm transition ease-in-out rounded-lg shadow-sm group text-light-body-text dark:text-dark-body-text hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue hover:cursor-pointer hover:text-dark-body-text duration-side-menu-transition-duration"
              :class="[index + 1 === users?.length ? '' : '']">
              <td class="w-5 px-6 py-3 tracking-wider">
                <input
                  v-model="user.checked"
                  type="checkbox"
                  class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration"
                  @change="toggleUserFromSelectedList(user, user.checked)">
              </td>
              <td
                v-for="header in headers.filter((el, i) => i > 0)"
                :key="header.id"
                class="px-6 py-3 font-medium tracking-wider whitespace-nowrap">
                <span v-if="header.type === 'header' && header.name !== 'team_manager' && header.name !== 'role_name'">
                  {{ user[header.name] }}
                </span>
                <input
                  v-else-if="header.name === 'team_manager' && selected_users.has(user.id)"
                  v-model="user.is_manager"
                  type="checkbox"
                  class="w-5 h-5 rounded form-checkbox text-blue-light focus:ring-blue group-hover:text-blue-dark">
                <div v-else-if="header.name === 'role_name'" class="">
                  <Listbox v-model="user.role" as="div" class="flex-shrink-0">
                    <ListboxLabel class="sr-only">
                      {{ $t("Role") }}
                    </ListboxLabel>
                    <div class="relative">
                      <ListboxButton
                        class="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 rounded-full bg-gray-50 whitespace-nowrap hover:bg-gray-100 sm:px-3">
                        <Tag
                          :class="[user.role === null ? 'text-gray-300' : 'text-gray-500', 'flex-shrink-0 h-5 w-5 sm:-ml-1']"
                          aria-hidden="true" />
                        <span :class="[user.role === null ? '' : 'text-gray-900', 'hidden truncate sm:ml-2 sm:block']">{{
                          user.role === null ? "Label" : $store.state.auth.user.organization.roles.find((x) => x.id == user.role.id).name
                        }}</span>
                      </ListboxButton>

                      <transition
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0">
                        <ListboxOptions
                          class="absolute right-0 z-10 py-3 mt-1 overflow-auto text-base text-gray-700 bg-white rounded-lg shadow w-52 max-h-56 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm hover:cursor-pointer hover:text-gray-700">
                          <ListboxOption
                            v-for="role in $store.state.auth.user.organization.roles"
                            :key="role.name"
                            v-slot="{ active }"
                            :value="role"
                            as="template">
                            <li :class="[active ? 'bg-gray-100' : 'bg-white', 'cursor-default select-none relative py-2 px-3']">
                              <div class="flex items-center">
                                <span class="block font-medium truncate">
                                  {{ role.name }}
                                </span>
                              </div>
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </div>
                  </Listbox>
                </div>

                <!-- todo: added logic for removing users -->
                <span v-else-if="updating" @click="() => console.warn('Not implemented')">{{ $t("Remove") }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Testing User Table End -->
      <button ref="updateTeam" style="display: none">{{ $t("Submit") }}</button>
    </Form>
    <Modal v-model:is-open="show_error_dialog">
      <template #form="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-4 text-red-400" aria-hidden="true" />
          </div>
          <p class="mt-2 text-xl text-gray-500">
            {{ error_message }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>
    <Modal v-model:is-open="is_required_email">
      <template #form="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-4 text-red-400" aria-hidden="true" />
          </div>
          <p class="mt-2 text-xl text-gray-500">
            {{ error_message }}
          </p>
          <div class="flex flex-row justify-between w-full gap-2 pt-2 mt-6">
            <button type="button" class="w-1/2 m-0 cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
            <button class="w-1/2 primary-btn" @click="emailFilterUsers()">
              <span>{{ $t("Continue") }}</span>
            </button>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import dataService from "@/services/data.service"

import Modal from "@/components/shared/Modal.vue"
import { ListboxLabel, Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue"
import { Tag, CircleAlert } from "lucide-vue-next"
import TextArea from "@/components/shared/TextArea.vue"
import TextInput from "@/components/shared/TextInput.vue"
import Users from "@/services/auth.service"
import SelectInput from "@/components/shared/SelectInput.vue"
import { Form } from "vee-validate"
import draggable from "vuedraggable"
import Spinner from "@/components/shared/LoadingSpinner.vue"

import { ALL_EMITTERS } from "@/store/constants/all_emitters"

import * as Yup from "yup"
const tableHeaders = [
  { type: "checkbox", id: "Checkbox", name: "_checkbox" },
  // { type: "header", id: "ID", name: "id" },
  { type: "header", id: "First Name", name: "first_name" },
  { type: "header", id: "Last Name", name: "last_name" },
  { type: "header", id: "Email", name: "email" },
  { type: "header", id: "Role", name: "role_name" },
  { type: "header", id: "Team", name: "team" },
  { type: "header", id: "Team Manager", name: "team_manager" },

  { type: "nonHeader", id: "Remove", name: "remove" }
]

export default {
  components: {
    Modal,
    Form,
    draggable,
    Spinner,
    SelectInput,
    ListboxLabel,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    Tag,
    CircleAlert,
    TextInput,
    TextArea
  },
  props: {
    team: {
      type: Object,
      required: true
    },

    updating: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const new_team_schema = Yup.object().shape({
      name: Yup.string().required(),
      description: Yup.string()
    })

    const initial_values = { name: props.team.name, description: props.team.description }

    return {
      new_team_schema,
      initial_values
    }
  },
  data() {
    return {
      sf_users: [],
      sf_users_filter: "",
      filtered_sf_users: [],

      // form data
      headers: tableHeaders,
      drag: false,
      error_message: null,
      show_error_dialog: false,
      is_required_email: false,
      users: [],
      team_managers: [],

      selected_users: new Map(),
      select_all_users: false,
      teams: [],
      selected_team: null,
      searched_user: "",
      is_loading: false
    }
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: "user header",
        disabled: false,
        ghostClass: "ghost",
        chosenClass: "chosen"
      }
    }
  },
  watch: {
    sf_users_filter() {
      if (this.sf_users_filter != null && this.sf_users_filter != "") {
        this.filtered_sf_users = this.sf_users.filter((u) => {
          return u["email"].toLowerCase().search(this.sf_users_filter) != -1 || u["name"].toLowerCase().search(this.sf_users_filter) != -1
        })
      } else {
        this.filtered_sf_users = this.sf_users
      }
    },
    select_all_users() {
      this.users = this.users.map((user) => {
        user["checked"] = this.select_all_users
        this.toggleUserFromSelectedList(user, this.select_all_users)
        return user
      })
    },
    selected_team() {
      if (this.selected_team) {
        const userlist = this.selected_team.users.filter((i) => !this.users.includes(i.id))
        userlist.map((user) => {
          user.role = this.$store.state.auth.user.organization.roles.find((x) => x.id == user.role)
        })
        this.users = userlist
      }
      if (!this.selected_team) {
        this.fetchUsers()
      }
    },
    searched_user() {
      if (!this.searched_user) {
        this.fetchUsers()
      }
      const users = this.users.filter((u) => {
        let searchedItem = this.searched_user.toLowerCase()
        let firstName = u.first_name.toLowerCase()
        let lastName = u.last_name.toLowerCase()
        if (firstName.includes(searchedItem) || lastName.includes(searchedItem)) {
          return u
        }
      })
      this.users = users
    }
  },

  activated() {
    this.register_listeners()
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Update Team"])
  },
  mounted() {
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Update Team"])
  },

  created() {
    this.register_listeners()
    this.fetchUsers()
    this.team.users.map((user) => {
      this.selected_users.set(user.id, user)
    })
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },

  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.UPDATE_TEAM, () => {
        this.$refs.updateTeam.click()
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.UPDATE_TEAM)
    },
    async fetchUsers() {
      const teamList = await dataService.getOrganizationTeams()
      this.teams = teamList.data.results

      const fetchedUsers = new Promise((resolve) => {
        Users.fetchUsers().then((res) => resolve(res.data.results))
      })
      this.users = await fetchedUsers
      if (this.users.length > 0) {
        this.users = this.users.map((u) => {
          let teams = []
          this.teams.map((team) => {
            team.users.map((user) => {
              if (user.id == u.id) {
                teams.push(team.name)
              }
              u["is_manager"] = false
              this.team.team_managers.map((id) => {
                if (u.id == id) {
                  u["is_manager"] = true
                }
              })
            })
          })
          u["team"] = this.team.name
          u["checked"] = this.selected_users.has(u.id)
          u["status"] = u.is_active ? u.is_active : false
          return u
        })
        this.$store.dispatch("ui/hideLoading")
      }
    },

    emailFilterUsers() {
      Array.from(this.selected_users.values()).map((user) => {
        if (!user.email) {
          this.selected_users.delete(user.id)
        }
      })
      this.$refs.updateTeam.click()
    },

    toggleUserFromSelectedList(user, selected) {
      if (selected) {
        this.selected_users.set(user.id, user)
      } else {
        this.selected_users.delete(user.id)
      }
    },

    updateTeam(values) {
      //Check is any required field
      this.show_error_dialog = false
      this.is_required_email = false
      let arrSelectedUsers = Array.from(this.selected_users.values())

      if (!values["name"] || values["name"].length == 0) {
        this.show_error_dialog = true
        this.error_message = this.$t("Team name is required")
        return
      } else if (arrSelectedUsers.length === 0) {
        this.show_error_dialog = true
        this.error_message = this.$t("Should select members")
      } else {
        let isExistManager = false
        let isExistEmptyEmail = false
        arrSelectedUsers.map((user) => {
          if (this.users.find((u) => user.id == u.id && u.checked && u.is_manager)) {
            isExistManager = true
          }
          if (!user.email) {
            isExistEmptyEmail = true
          }
        })
        if (!isExistManager) {
          this.show_error_dialog = true
          this.error_message = this.$t("Should select team manager")
          return
        } else if (isExistEmptyEmail) {
          this.is_required_email = true
          this.error_message = this.$t(
            "You can not save a team with a Team Member that does not have an email address. When you click on CONTINUE, members who do not have an email address will be excluded from the team"
          )
          return
        }
      }

      //If all required field not emty
      if (!this.show_error_dialog && !this.is_required_email) {
        this.show_error_dialog = false
        this.team_managers = []
        let users = []
        Array.from(this.selected_users.values()).map((user) => {
          this.users.map((x) => {
            if (user.id == x.id) {
              if (x.is_manager) {
                this.team_managers.push(x.id)
              }
            }
          })
          let u
          this.users.map((x) => {
            if (user.id == x.id) {
              if (x.role) {
                u = this.$utils.clone(x)
                u["role"] = x.role.id
                users.push(u)
                //user.role = user.role.id
              } else {
                users.push(x)
              }
            }
          })
        })
        let updatedTeam = {
          organization: this.$store.state.organization.id,
          id: this.team.id,
          name: values["name"],
          description: values["description"],
          users: users,
          team_managers: this.team_managers
        }
        dataService
          .updateOrganizationTeam(updatedTeam)
          .then(() => {
            this.$toast.open({
              message: this.$t("Team") + ` "${values.name}" ` + this.$t("updated successfully"),
              position: "top",
              type: "success",
              duration: 3000
            })
            this.$emitter.emit(ALL_EMITTERS.REFRESH_TEAMS)
          })
          .catch(() => {
            this.$toast.open({
              message: this.$t("Error updating a Team, please try again!"),
              position: "top",
              type: "error",
              duration: 5000
            })
          })
      } else {
        return
      }
    }
  }
}
</script>
